// Central content data extracted from the NARA mockup. Prices/shipping are EUR (Europe only).

export const NAV_LINKS = [
  { label: "Products", to: "/product" },
  { label: "Science", to: "/science" },
  { label: "Ingredients", to: "/ingredients" },
  { label: "Benefits", to: "/benefits" },
  { label: "Bundles", to: "/bundles" },
  { label: "About", to: "/about" },
  { label: "Journal", to: "/journal" },
];

export const BENEFITS = [
  { icon: "Leaf", title: "Gut Health", desc: "Supports digestive balance and a thriving microbiome." },
  { icon: "Zap", title: "Energy", desc: "Helps convert nutrients into steady, sustainable energy." },
  { icon: "Target", title: "Focus", desc: "Supports mental clarity, concentration and cognitive performance." },
  { icon: "Sparkles", title: "Vitality", desc: "Nourishes your cells to help you feel vibrant and full of life." },
  { icon: "RefreshCw", title: "Recovery", desc: "Supports post-activity recovery and muscle repair." },
  { icon: "Infinity", title: "Longevity", desc: "Nourishes long-term health for a better tomorrow." },
  { icon: "ShieldCheck", title: "Immune Resilience", desc: "Supports natural defenses and seasonal immune strength." },
];

// In-depth benefit systems (expandable). `group` maps to INGREDIENT_GROUPS index.
export const BENEFIT_SYSTEMS = [
  {
    icon: "Leaf",
    title: "Gut Health",
    tagline: "Digestive balance & a thriving microbiome",
    detail:
      "Your gut is the foundation of whole-body wellness. We rebuild and protect the gut lining with L-Glutamine and Zinc Carnosine, feed your microbiome with prebiotic inulin, and seed it with clinically-studied probiotic and postbiotic strains for lasting digestive balance.",
    group: 0,
  },
  {
    icon: "Zap",
    title: "Energy",
    tagline: "Clean, all-day cellular energy",
    detail:
      "Steady, sustainable energy starts at the cellular level. A full spectrum of methylated B-vitamins, electrolytes and mitochondrial cofactors like Acetyl-L-Carnitine, Alpha-Lipoic Acid and CoQ10 help your cells convert nutrients into clean energy — without the crash.",
    group: 1,
  },
  {
    icon: "Brain",
    title: "Focus",
    tagline: "Calm clarity & cognitive performance",
    detail:
      "Sharper thinking, on demand. Citicoline and Bacopa support memory and processing speed, Lion's Mane nurtures nerve growth, while L-Theanine and Rhodiola deliver calm, resilient focus under pressure.",
    group: 2,
  },
  {
    icon: "Sparkles",
    title: "Vitality",
    tagline: "Hormone balance, mood & drive",
    detail:
      "Feel vibrant and balanced. Vitamin D3, K2 and zinc support healthy hormones, while gelatinized Maca and Saffron are clinically associated with improved mood, drive and overall vitality — gentle enough for every day.",
    group: 3,
  },
  {
    icon: "RefreshCw",
    title: "Recovery",
    tagline: "Joints, recovery & structural health",
    detail:
      "Bounce back stronger. Hydrolyzed collagen, MSM and glycine support joints, skin and connective tissue, while beta-alanine and electrolytes power endurance and post-activity recovery.",
    group: 4,
  },
  {
    icon: "Infinity",
    title: "Longevity",
    tagline: "Cellular renewal & healthy aging",
    detail:
      "Age on your terms. Omega-3 and trans-resveratrol calm inflammation and protect cells, while next-generation actives like Urolithin A, Spermidine and Astaxanthin support mitochondrial renewal and healthy aging.",
    group: 5,
  },
  {
    icon: "ShieldCheck",
    title: "Immune Resilience",
    tagline: "Defenses & stress resilience",
    detail:
      "Defend and adapt. Functional mushrooms Reishi and Chaga modulate immune response and provide antioxidant defense, helping your body stay resilient through stress and changing seasons.",
    group: 6,
  },
];

export const SCIENCE_STATS = [
  { icon: "FlaskConical", value: "12+", label: "Clinical Studies", desc: "Backed by independent research and trials." },
  { icon: "Target", value: "6", label: "Core Benefits", desc: "Targeted daily support for modern wellness." },
  { icon: "Repeat", value: "1", label: "Daily Ritual", desc: "One simple sachet. Real, lasting impact." },
  { icon: "ShieldCheck", value: "100%", label: "Transparency", desc: "No proprietary blends. Full ingredient disclosure." },
];

export const PRINCIPLES = [
  { icon: "Brain", title: "Clinically Informed", desc: "Every ingredient is selected based on human research and real-world outcomes." },
  { icon: "Waves", title: "High Bioavailability", desc: "Advanced forms and delivery systems for optimal absorption and effectiveness." },
  { icon: "Combine", title: "Synergistic Formulas", desc: "Ingredients work together intentionally to amplify benefits and support whole-body wellness." },
  { icon: "Sparkles", title: "Clean & Transparent", desc: "No unnecessary fillers. No hidden blends. Just what your body needs to thrive." },
];

export const INGREDIENTS = [
  { icon: "Microscope", name: "Probiotics", desc: "Supports gut balance, digestion and overall immune health." },
  { icon: "Sprout", name: "Functional Mushrooms", desc: "Promotes clarity, immunity and long-term resilience." },
  { icon: "Moon", name: "Magnesium", desc: "Supports calm, recovery and quality sleep." },
  { icon: "Fish", name: "Omega-3", desc: "Supports heart, brain and inflammation balance." },
  { icon: "Flower2", name: "Adaptogens", desc: "Helps the body adapt to stress and restore natural balance." },
];

export const STEPS = [
  { icon: "Scissors", step: "01", title: "Tear", desc: "Tear open one sachet along the dotted line." },
  { icon: "Droplets", step: "02", title: "Mix", desc: "Pour into 200ml of water. Stir or shake until fully dissolved." },
  { icon: "GlassWater", step: "03", title: "Drink", desc: "Enjoy your daily wellness boost." },
  { icon: "Repeat", step: "04", title: "Repeat", desc: "Make it a daily ritual for lasting wellness." },
];

export const TRUST_BADGES = [
  { icon: "Truck", title: "Free Shipping", desc: "On all EU orders over €75" },
  { icon: "BadgeCheck", title: "30-Day Guarantee", desc: "Love it or your money back" },
  { icon: "Lock", title: "Secure Checkout", desc: "Safe and encrypted" },
  { icon: "FlaskConical", title: "Third-Party Tested", desc: "Purity & potency verified" },
  { icon: "Award", title: "GMP Certified", desc: "European manufacturing standards" },
  { icon: "Microscope", title: "Science-Backed", desc: "Formulated with purpose" },
];

export const TESTIMONIALS = [
  { quote: "NARA has become a non-negotiable part of my morning. I feel more balanced, focused and ready for anything.", name: "Lauren M.", location: "London, UK", helpful: 34 },
  { quote: "Cleaner energy, better digestion and a noticeable lift in my day-to-day. Highly recommend.", name: "Daniel R.", location: "Berlin, DE", helpful: 41 },
  { quote: "I've tried many supplements, but NARA is in a league of its own. Premium quality you can feel.", name: "Maya S.", location: "Amsterdam, NL", helpful: 29 },
];

export const FEATURED_IN = ["WELL+GOOD", "mindbodygreen", "Women's Health", "Forbes", "Clean Eating"];

export const PILLARS = [
  { icon: "Eye", title: "Clarity", desc: "Transparent formulas. Honest communication. Nothing hidden." },
  { icon: "ShieldCheck", title: "Integrity", desc: "Clinically informed. Quality assured. Always to European standards." },
  { icon: "Zap", title: "Convenience", desc: "Designed for real life. Portable, effortless, effective." },
  { icon: "Layers", title: "Depth", desc: "Beyond the basics. Targeted support for lasting impact." },
];

export const JOURNAL_CATEGORIES = [
  { label: "All Articles", count: 12 },
  { label: "Gut Health", count: 4 },
  { label: "Longevity", count: 3 },
  { label: "Nutrition", count: 3 },
  { label: "Lifestyle", count: 2 },
];

export const FEATURED_ARTICLE = {
  category: "Gut Health",
  read: "8 MIN READ",
  title: "The Foundation of Daily Wellness",
  subtitle: "Why gut health is at the core of energy, immunity, clarity and long-term vitality.",
  image: "/images/gen/journal_featured.png",
};

export const ARTICLES = [
  { category: "GUT HEALTH", read: "6 MIN READ", title: "Beyond Digestion: The Gut Microbiome and Whole-Body Wellness", image: "/images/gen/article_gut.png" },
  { category: "LONGEVITY", read: "7 MIN READ", title: "Longevity by Design: Small Habits, Lasting Impact", image: "/images/gen/article_longevity.png" },
  { category: "NUTRITION", read: "5 MIN READ", title: "Nutritional Synergy: Ingredients That Work Better Together", image: "/images/gen/article_nutrition.png" },
];

export const PRODUCT_BADGES = [
  "7 Systems Supported",
  "Science-Led Formulations",
  "Premium Ingredients",
  "Gentle & Bioavailable",
  "Daily Habit, Lasting Impact",
];
